import React from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { formatMoneyPeriodHeader, getPeriodKey } from "@common/helpers/reports";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { ViewConfig } from "@lib/views";
import { buildClassColumnGrouping } from "@design-system/ViewTable/core/classColumnGrouping";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { makeClassColumnHeaderCell } from "../ClassColumnHeaderCell";
import { isEliminationsColumn } from "../consolidatedColumns";
import {
    getColumnPinOptions,
    getLabelColumnMaxSize,
} from "../singleColumnReport";
import { makeProfitLossPeriodCell } from "./ProfitLossPeriodCell";
import { getProfitLossCellClassName } from "./profitLossCellClassName";
import { ProfitLossViewRow } from "./profitLossViewTypes";

// ---------------------------------------------------------------------------
// Column sizing constants
// ---------------------------------------------------------------------------

const PNL_TITLE_COLUMN_ID = "title";
const TITLE_MIN_SIZE = 240;
const TITLE_MAX_SIZE = 1200;
const PERIOD_MIN_SIZE = 120;
const PERIOD_MAX_SIZE = 200;

// ---------------------------------------------------------------------------
// Title cell
// The expand/collapse button is injected by buildColumns (via ExpandCollapseCell)
// when this column is set as config.nesting.expandColumn.
// ---------------------------------------------------------------------------

const renderTitleCell = React.memo(function ProfitLossTitleCell({
    row,
}: CellContext<
    ViewTableFeatures,
    ProfitLossViewRow,
    unknown
>): React.ReactElement {
    const { title, isTotal, isSummary } = row.original;
    return (
        <ViewTableCell
            className={getProfitLossCellClassName(isTotal, isSummary)}
        >
            {title}
        </ViewTableCell>
    );
});

// ---------------------------------------------------------------------------
// Period value cell factory
// ---------------------------------------------------------------------------

function makePeriodCell(options: {
    period: ReportPeriodWithLabel;
    periodIndex: number;
    nonTotalPeriods: ReportPeriodWithLabel[];
    nonTotalIndex: number;
}) {
    return makeProfitLossPeriodCell(options);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function partitionPeriods(periods: ReportPeriodWithLabel[]) {
    const nonTotalPeriods = periods.filter((p) => !p.isTotal);
    const totalPeriods = periods.filter((p) => p.isTotal);
    return { nonTotalPeriods, totalPeriods };
}

// ---------------------------------------------------------------------------
// Public builders
// ---------------------------------------------------------------------------

type PnlColumnRecord = Record<
    string,
    ViewConfig<ProfitLossViewRow, string, never>["columns"][string]
>;

/**
 * Builds an ephemeral ViewConfig driven entirely by the period list.
 * Uses string as TColumn since column IDs are dynamically derived from periods.
 */
export function buildProfitLossConfig(
    periods: ReportPeriodWithLabel[],
    numberFormat?: NumberFormatValues,
): ViewConfig<ProfitLossViewRow, string, never> {
    const { nonTotalPeriods } = partitionPeriods(periods);
    const classGrouping = buildClassColumnGrouping(periods);

    const columns: PnlColumnRecord = {
        [PNL_TITLE_COLUMN_ID]: {
            name: "Name",
            header: "",
            accessorKey: "title",
            cell: renderTitleCell,
            minSize: TITLE_MIN_SIZE,
            maxSize: getLabelColumnMaxSize({
                periods,
                labelMaxSize: TITLE_MAX_SIZE,
            }),
            immutable: true,
        },
    };

    let nonTotalIndex = 0;
    periods.forEach((period, periodIndex) => {
        const isTotalPeriod = Boolean(period.isTotal);
        const columnId = isTotalPeriod
            ? `total-${getPeriodKey(period)}`
            : `period-${getPeriodKey(period)}`;
        const currentNonTotalIndex = isTotalPeriod ? -1 : nonTotalIndex++;

        const headerCell = makeClassColumnHeaderCell(period, numberFormat);

        const groupPath = classGrouping?.groupPathByColumnId[columnId];
        const headerTierDepth =
            classGrouping?.headerTierDepthByColumnId[columnId];

        columns[columnId] = {
            name: period.label,
            header: formatMoneyPeriodHeader(period, numberFormat),
            headerCell,
            accessorKey: "values",
            cell: makePeriodCell({
                period,
                periodIndex,
                nonTotalPeriods,
                nonTotalIndex: currentNonTotalIndex,
            }),
            minSize: PERIOD_MIN_SIZE,
            maxSize: PERIOD_MAX_SIZE,
            groupPath,
            headerTierDepth,
        };
    });

    return {
        columns,
        groups: classGrouping?.groups,
        nesting: {
            enabled: true,
            expandColumn: PNL_TITLE_COLUMN_ID,
            childrenKey: "children",
        },
    };
}

/**
 * Builds an ephemeral View object (never persisted to DB).
 * Eliminations and total period columns are pinned right; no selection or
 * bulk-actions.
 */
export function buildProfitLossView(periods: ReportPeriodWithLabel[]): View {
    const { nonTotalPeriods, totalPeriods } = partitionPeriods(periods);
    const unpinnedPeriods = nonTotalPeriods.filter(
        (period) => !isEliminationsColumn(period),
    );
    const eliminationPeriods = nonTotalPeriods.filter(isEliminationsColumn);

    return {
        type: ViewType.PROFIT_LOSS,
        columns: [
            {
                id: PNL_TITLE_COLUMN_ID,
                options: getColumnPinOptions({ periods, pin: "left" }),
            },
            ...unpinnedPeriods.map((period) => ({
                id: `period-${getPeriodKey(period)}`,
            })),
            ...eliminationPeriods.map((period) => ({
                id: `period-${getPeriodKey(period)}`,
                options: getColumnPinOptions({ periods, pin: "right" }),
            })),
            ...totalPeriods.map((period) => ({
                id: `total-${getPeriodKey(period)}`,
                options: getColumnPinOptions({ periods, pin: "right" }),
            })),
        ],
        filters: {},
        options: {
            selection: false,
            bulkActions: false,
            density: ViewDensity.LG,
        },
    };
}
